import { motion } from "framer-motion";
import { ExternalLink, X, Youtube } from "lucide-react";
import { FaInstagram, FaLinkedinIn, FaTiktok } from "react-icons/fa";
import { FaThreads } from "react-icons/fa6";
import type { LinkPreviewInfo } from "@/lib/link-preview";

function PlatformIcon({ kind }: { kind: LinkPreviewInfo["kind"] }) {
  switch (kind) {
    case "instagram":
      return <FaInstagram className="w-3 h-3 text-pink-600 shrink-0" />;
    case "tiktok":
      return <FaTiktok className="w-3 h-3 text-foreground shrink-0" />;
    case "twitter":
      return <span className="text-[10px] font-black text-foreground shrink-0">𝕏</span>;
    case "threads":
      return <FaThreads className="w-3 h-3 text-foreground shrink-0" />;
    case "linkedin":
      return <FaLinkedinIn className="w-3 h-3 text-sky-700 shrink-0" />;
    default:
      return null;
  }
}

const CARD_COLORS: Record<NonNullable<LinkPreviewInfo["kind"]>, string> = {
  youtube: "card-lavender",
  instagram: "card-blush",
  tiktok: "card-sky",
  twitter: "card-sage",
  threads: "card-terra",
  linkedin: "card-golden",
};

export function LinkPreviewCard({
  preview,
  href,
  onClear,
}: {
  preview: LinkPreviewInfo;
  href: string;
  onClear: () => void;
}) {
  const cardColor = preview.kind ? CARD_COLORS[preview.kind] : "card-lavender";

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      className={`mx-4 mt-3 rounded-2xl overflow-hidden border border-white/60 ${cardColor} relative`}
    >
      <div className="flex items-center gap-3 p-3">
        {preview.thumbnailUrl ? (
          <div className="relative w-20 h-14 rounded-xl overflow-hidden shrink-0 bg-black/10">
            <img src={preview.thumbnailUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-7 h-7 rounded-full bg-black/40 flex items-center justify-center">
                <div className="w-0 h-0 border-y-4 border-y-transparent border-l-[8px] border-l-white ml-1" />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-14 h-14 rounded-xl bg-white/50 flex items-center justify-center shrink-0 text-xl">
            {preview.kind === "instagram" ? "📸" : preview.kind === "tiktok" ? "🎵" : "💬"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {preview.kind === "youtube" ? (
              <Youtube className="w-3 h-3 text-red-500 shrink-0" />
            ) : (
              <PlatformIcon kind={preview.kind} />
            )}
            <span className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">
              {preview.label}
            </span>
          </div>
          <p className="text-xs text-foreground/60 truncate">{preview.subtitle}</p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline mt-0.5"
          >
            <ExternalLink className="w-2.5 h-2.5" /> Open link
          </a>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="w-6 h-6 rounded-full bg-white/60 flex items-center justify-center text-foreground/40 hover:text-foreground transition-colors shrink-0"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
}
